import React, { useState, useEffect } from "react";
import {
  LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import ExportModal from "../components/ExportModal";
import { GROWTH_COLUMNS, DEFAULT_KEYS } from "../config/exportColumns";
import { getGrowthData, getTopCategories, getEventsWithData, getGeopolitical } from "../services/api";
import { colors as theme } from "../theme";
import { ALIGN_COLOR, ALIGN_BG, ALIGN_LABEL, ALIGN_SOURCE_NOTE, intensityBg, mergeGeoYoY } from "../config/geopolitics";
import { mergeYoY, growthPct } from "../config/growthMetrics";
import { Building2, Plus, Repeat, TrendingDown, Globe, Package, Download, Info } from "lucide-react";
import { useDefaultEvent } from "../hooks/useDefaultEvent";
import { EventSelect } from "../components/EventSelect";
import { FilterBar, FilterSelect, FilterClear, FilterSpacer, filterSelectStyle } from "../components/FilterBar";

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";

const CY               = new Date().getFullYear();
const ALL_YEARS_DEFAULT = CY - 3;
const SHOW_COLORS      = theme.chart;
const GTABS = [
  "Competitor Show Growth",
  "Country Growth",
  "Category Growth",
  "New / Returning / Dropped",
];

// pct here is a GROWTH rate ((a-b)/b), not a share — aliased from the shared module.
const pct = growthPct;

// ── Shared presentational components ──────────────────────────────────────────

function ChartCard({ title, children, style }) {
  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"16px 18px", ...style }}>
      {title && <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>{title}</div>}
      {children}
    </div>
  );
}

function DataTable({ title, rows, unit }) {
  return (
    <ChartCard title={title}>
      <table className="ct">
        <thead>
          <tr>
            <th>Year</th><th>{unit}</th><th>% of Total</th><th>YoY Growth</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.y}>
              <td style={{ fontWeight:600 }}>{r.y}</td>
              <td style={{ fontFamily:M, fontWeight:700 }}>{r.v.toLocaleString()}</td>
              <td>{r.pctt}%</td>
              <td style={{ fontWeight:700, color: r.g == null ? "#818084" : r.g > 0 ? "#16a34a" : r.g < 0 ? "#C1121F" : "#818084" }}>
                {r.g == null ? "—" : `${r.g > 0 ? "+" : ""}${r.g}%`}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop:"2px solid #D8DADC" }}>
            <td style={{ fontWeight:700 }}>Total</td>
            <td style={{ fontFamily:M, fontWeight:800 }}>{rows.reduce((a, r) => a + r.v, 0).toLocaleString()}</td>
            <td>—</td><td>—</td>
          </tr>
        </tbody>
      </table>
    </ChartCard>
  );
}

function EmptyTab({ label }) {
  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC",
      padding:"48px 24px", textAlign:"center", color:"#818084" }}>
      <div style={{ fontSize:13, fontWeight:600 }}>{label}</div>
      <div style={{ fontSize:11, marginTop:4 }}>Data populates after enrichment jobs complete.</div>
    </div>
  );
}

function NoticeBanner({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px",
      borderRadius:8, background:"#FFFBEB", border:"1px solid #FCD34D",
      fontSize:11, color:"#92400E", marginBottom:16 }}>
      <Info size={14} style={{ flexShrink:0 }} />
      {children}
    </div>
  );
}

// ── Tab components ─────────────────────────────────────────────────────────────

// hasComparison is decided by the page (shows reporting both years), not by year
// count alone — two years of data with no overlapping shows is not comparable.
function CompetitorShowTab({ byYear, selShows, showSeries, growthByShow, yoy, YEARS, hasComparison, partialBasis }) {
  const currYear = YEARS[YEARS.length - 1];

  const showTotals = selShows
    .map(s => ({ name: s.name, total: s.yearData.find(d => d.y === currYear)?.total || 0 }))
    .sort((a, b) => b.total - a.total);

  return (
    <>
      {hasComparison ? (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
          <ChartCard title="Company Count by Year">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={byYear}>
                <XAxis dataKey="y" tick={{ fontSize:10 }} stroke="#D8DADC" />
                <YAxis tick={{ fontSize:10 }} stroke="#D8DADC" />
                <Tooltip contentStyle={{ fontSize:11, fontFamily:F }} />
                <Line type="monotone" dataKey="total" stroke="#E8431A" strokeWidth={2.5} dot={{ r:4 }} name="Companies" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Company Count by Competitor Show">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={showSeries}>
                <XAxis dataKey="y" tick={{ fontSize:10 }} stroke="#D8DADC" />
                <YAxis tick={{ fontSize:10 }} stroke="#D8DADC" />
                <Tooltip contentStyle={{ fontSize:11, fontFamily:F }} />
                {selShows.map((s, i) => (
                  <Line key={s.key} type="monotone" dataKey={s.key} name={s.name}
                    stroke={SHOW_COLORS[i % SHOW_COLORS.length]} strokeWidth={2} dot={{ r:3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <div style={{ display:"flex", gap:12, justifyContent:"center", marginTop:6, flexWrap:"wrap" }}>
              {selShows.map((s, i) => (
                <div key={s.key} style={{ display:"flex", alignItems:"center", gap:4, fontSize:9.5, color:"#818084" }}>
                  <div style={{ width:10, height:3, borderRadius:2, background:SHOW_COLORS[i % SHOW_COLORS.length] }} />
                  {s.name}
                </div>
              ))}
            </div>
          </ChartCard>

          <ChartCard title="Growth Rate (%) by Show (latest YoY)">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={growthByShow}>
                <XAxis dataKey="name" tick={{ fontSize:9 }} stroke="#D8DADC" />
                <YAxis tick={{ fontSize:9 }} stroke="#D8DADC" />
                <Tooltip contentStyle={{ fontSize:11, fontFamily:F }} />
                <Bar dataKey="g" radius={[3, 3, 0, 0]} name="Growth %">
                  {growthByShow.map((e, i) => <Cell key={i} fill={e.g >= 0 ? "#22c55e" : "#C1121F"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      ) : (
        <div style={{ marginBottom:14 }}>
          <ChartCard title={`Company Count by Competitor Show (${currYear})`}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={showTotals}>
                <XAxis dataKey="name" tick={{ fontSize:9 }} stroke="#D8DADC" />
                <YAxis tick={{ fontSize:10 }} stroke="#D8DADC" />
                <Tooltip contentStyle={{ fontSize:11, fontFamily:F }} />
                <Bar dataKey="total" radius={[3, 3, 0, 0]} name="Companies">
                  {showTotals.map((_, i) => <Cell key={i} fill={SHOW_COLORS[i % SHOW_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      <ChartCard title="Year-over-Year Summary">
        <table className="ct">
          <thead>
            <tr>
              <th>Metric</th>
              {byYear.map(d => <th key={d.y}>{d.y}</th>)}
              {/* Year columns are true totals for each year; the change columns are
                  like-for-like (shows reporting both years). Say so when they differ,
                  otherwise "5 → 40, change +15" reads as an error. */}
              {hasComparison && (
                <>
                  <th>Change{partialBasis ? " (like-for-like)" : ""}</th>
                  <th>Change %{partialBasis ? " (like-for-like)" : ""}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {yoy.map(r => (
              <tr key={r.m}>
                <td style={{ fontWeight:700 }}>{r.m}</td>
                {r.vals.map((v, i) => <td key={i} style={{ fontFamily:M }}>{v.toLocaleString()}</td>)}
                {hasComparison && (
                  <>
                    <td style={{ fontWeight:700, color: r.ch == null ? "#818084" : r.ch < 0 ? "#C1121F" : "#16a34a" }}>
                      {r.ch == null ? "—" : `${r.ch >= 0 ? "+" : ""}${r.ch.toLocaleString()}`}
                    </td>
                    <td style={{ fontWeight:700, color: r.chp == null ? "#818084" : r.chp < 0 ? "#C1121F" : "#16a34a" }}>
                      {r.chp == null ? "—" : `${r.chp >= 0 ? "+" : ""}${r.chp}%`}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </ChartCard>
    </>
  );
}

// Country Growth — combines the YoY country comparison with the geopolitical
// alignment lens (Region + Friendly/Unfriendly), so it's one country story:
// chart + alignment summary + an enriched, scannable table.
function CountryGrowthTab({ rows, loading, currYear, prevYear }) {
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return <div style={{ padding:"48px 0", textAlign:"center", color:"#818084", fontSize:12 }}>Loading country data…</div>;
  }
  if (rows.length === 0) {
    return <EmptyTab label="No country data for the selected event and years." />;
  }

  const maxCurr   = Math.max(...rows.map(r => r.curr), 1);
  const chartData = rows.slice(0, 12);
  const visible   = showAll ? rows : rows.slice(0, 25);

  // Alignment summary — where are the gains coming from?
  const sumBy = (flag, key) => rows.filter(r => r.alignment_flag === flag).reduce((a, r) => a + r[key], 0);
  const summary = ["friendly", "unfriendly", "unknown"].map(f => {
    const curr = sumBy(f, "curr"), prev = sumBy(f, "prev");
    return { f, curr, prev, g: prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null };
  }).filter(s => s.curr > 0 || s.prev > 0);

  const growthCell = (g, delta) => {
    if (g == null) return <span style={{ color:"#818084" }}>New</span>;
    const c = g > 0 ? "#16a34a" : g < 0 ? "#C1121F" : "#818084";
    return (
      <span style={{ fontWeight:700, color:c }}>
        {g > 0 ? "+" : ""}{g}%
        <span style={{ fontWeight:400, color:"#AFABAB", marginLeft:5 }}>{delta > 0 ? "+" : ""}{delta.toLocaleString()}</span>
      </span>
    );
  };

  const badge = (flag) => (
    <span style={{ display:"inline-block", padding:"2px 7px", borderRadius:4, fontSize:10, fontWeight:600,
      color: ALIGN_COLOR[flag] || "#818084", background: ALIGN_BG[flag] || "#F4F4F5" }}>
      {ALIGN_LABEL[flag] || flag}
    </span>
  );

  const chartTitle = prevYear ? `Top Countries — ${prevYear} vs ${currYear}` : `Top Countries — ${currYear}`;

  return (
    <>
      {/* Row 1: YoY chart + alignment summary */}
      <div style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr", gap:14, marginBottom:14 }}>
        <ChartCard title={chartTitle}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <XAxis dataKey="country" tick={{ fontSize:9 }} stroke="#D8DADC"
                interval={0} angle={-30} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize:9 }} stroke="#D8DADC" />
              <Tooltip contentStyle={{ fontSize:11, fontFamily:F }} />
              {prevYear && <Bar dataKey="prev" fill="#AFABAB" radius={[2, 2, 0, 0]} name={String(prevYear)} />}
              <Bar dataKey="curr" fill="#E8431A" radius={[2, 2, 0, 0]} name={String(currYear)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {summary.map(s => (
            <div key={s.f} style={{ background:"#fff", border:"1px solid #D8DADC", borderRadius:10,
              padding:"12px 14px", borderLeft:`3px solid ${ALIGN_COLOR[s.f]}` }}>
              <div style={{ fontSize:10, color:"#818084", fontWeight:600 }}>{ALIGN_LABEL[s.f]} markets</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:8, marginTop:3 }}>
                <span style={{ fontSize:19, fontWeight:800, fontFamily:M, letterSpacing:-0.5 }}>{s.curr.toLocaleString()}</span>
                {s.g != null && (
                  <span style={{ fontSize:11, fontWeight:700, color: s.g > 0 ? "#16a34a" : s.g < 0 ? "#C1121F" : "#818084" }}>
                    {s.g > 0 ? "+" : ""}{s.g}%
                  </span>
                )}
              </div>
              <div style={{ fontSize:9, color:"#AFABAB", marginTop:1 }}>
                exhibitors in {currYear}{prevYear ? ` · vs ${prevYear}` : ""}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Row 2: enriched detail table */}
      <ChartCard title="Country Growth Detail">
        <div style={{ overflowX:"auto" }}>
          <table className="ct">
            <thead>
              <tr>
                <th>#</th><th>Country</th><th>Region</th><th>Alignment</th>
                {prevYear && <th style={{ textAlign:"right" }}>{prevYear}</th>}
                <th style={{ textAlign:"right" }}>{currYear}</th>
                <th style={{ textAlign:"right" }}>Growth</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.country}>
                  <td style={{ color:"#818084", fontFamily:M }}>{i + 1}</td>
                  <td style={{ fontWeight:600 }}>{r.country}</td>
                  <td style={{ color:"#818084" }}>{r.region}</td>
                  <td>{badge(r.alignment_flag)}</td>
                  {prevYear && (
                    <td style={{ fontFamily:M, textAlign:"right", background:intensityBg(r.prev, maxCurr) }}>{r.prev.toLocaleString()}</td>
                  )}
                  <td style={{ fontFamily:M, fontWeight:700, textAlign:"right", background:intensityBg(r.curr, maxCurr) }}>{r.curr.toLocaleString()}</td>
                  <td style={{ textAlign:"right" }}>{growthCell(r.g, r.delta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > 25 && (
          <div style={{ textAlign:"center", marginTop:10 }}>
            <button onClick={() => setShowAll(v => !v)} style={{
              padding:"6px 14px", borderRadius:7, border:"1px solid #D8DADC", background:"#fff",
              fontSize:11, fontWeight:600, color:"#3F3F46", cursor:"pointer", fontFamily:F }}>
              {showAll ? "Show top 25" : `Show all ${rows.length} countries`}
            </button>
          </div>
        )}

        <div style={{ fontSize:9, color:"#AFABAB", marginTop:10 }}>{ALIGN_SOURCE_NOTE}</div>
      </ChartCard>
    </>
  );
}

function CategoryGrowthTab({ catRows, currYear }) {
  if (catRows.length === 0) {
    return <EmptyTab label="No category data for the selected event and years." />;
  }
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
      <ChartCard title={`Top Product Categories${currYear ? ` (${currYear})` : ""}`}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={catRows} layout="vertical">
            <XAxis type="number" tick={{ fontSize:9 }} stroke="#D8DADC" />
            <YAxis type="category" dataKey="key" tick={{ fontSize:9 }} stroke="#D8DADC" width={120} />
            <Tooltip contentStyle={{ fontSize:11, fontFamily:F }} />
            <Bar dataKey="curr" fill="#F18F18" radius={[0, 3, 3, 0]} name="Companies" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Category Growth Detail">
        <table className="ct">
          <thead>
            <tr><th>#</th><th>Category</th><th>Companies</th><th>YoY Growth</th></tr>
          </thead>
          <tbody>
            {catRows.map((c, i) => (
              <tr key={c.key}>
                <td style={{ color:"#818084", fontFamily:M }}>{i + 1}</td>
                <td style={{ fontWeight:600 }}>{c.key}</td>
                <td style={{ fontFamily:M, fontWeight:700 }}>{c.curr.toLocaleString()}</td>
                <td style={{ fontWeight:700, color: c.g == null ? "#818084" : c.g >= 0 ? "#16a34a" : "#C1121F" }}>
                  {c.g == null ? "New" : `${c.g >= 0 ? "+" : ""}${c.g}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ChartCard>
    </div>
  );
}

function NRDTab({ byYear, newTbl, retTbl, dropTbl }) {
  const LEGEND = [["New", "#22c55e"], ["Returning", "#960659"], ["Dropped", "#C1121F"]];
  return (
    <>
      <ChartCard title="New vs Returning vs Dropped by Year" style={{ marginBottom:14 }}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={byYear}>
            <XAxis dataKey="y" tick={{ fontSize:10 }} stroke="#D8DADC" />
            <YAxis tick={{ fontSize:9 }} stroke="#D8DADC" />
            <Tooltip contentStyle={{ fontSize:11, fontFamily:F }} />
            <Bar dataKey="newCo"     stackId="a" fill="#22c55e" name="New" />
            <Bar dataKey="returning" stackId="a" fill="#960659" name="Returning" />
            <Bar dataKey="dropped"   stackId="a" fill="#C1121F" name="Dropped" />
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display:"flex", gap:14, justifyContent:"center", marginTop:6 }}>
          {LEGEND.map(([label, color]) => (
            <div key={label} style={{ display:"flex", alignItems:"center", gap:4, fontSize:9.5, color:"#818084" }}>
              <div style={{ width:10, height:10, borderRadius:2, background:color }} />{label}
            </div>
          ))}
        </div>
      </ChartCard>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
        <DataTable title="New Companies by Year"       rows={newTbl}  unit="New" />
        <DataTable title="Returning Companies by Year" rows={retTbl}  unit="Returning" />
        <DataTable title="Dropped Companies by Year"   rows={dropTbl} unit="Dropped" />
      </div>
    </>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function GrowthPage() {
  const {
    selectedEvent, setSelectedEvent, changeEvent, nav,
    currentRole, setCurrentRole, currentUser,
    notifs, setNotifs, notifOpen, setNotifOpen,
    events, showToast,
  } = useApp();

  useDefaultEvent();

  const [gShow,       setGShow]       = useState("All");
  const [gYear,       setGYear]       = useState(ALL_YEARS_DEFAULT);
  const [growthTab,   setGrowthTab]   = useState(GTABS[0]);
  const [apiShows,    setApiShows]    = useState(null);
  const [pendingCount, setPendingCount] = useState(0);   // editions awaiting enrichment
  const [loading,       setLoading]       = useState(true);
  const [eventsWithData, setEventsWithData] = useState(null); // null = loading
  const [countryRows,    setCountryRows]    = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [catRows,        setCatRows]        = useState([]);
  const [exportCfg,      setExportCfg]       = useState(null);

  const eventName = events?.find(e => e.ite_event_id === selectedEvent)?.name
    || (selectedEvent ? selectedEvent : "All Events");

  // Load once: which ITE events have competitor data (for optgroup grouping).
  useEffect(() => {
    getEventsWithData()
      .then(ids => setEventsWithData(new Set(Array.isArray(ids) ? ids : [])))
      .catch(() => setEventsWithData(new Set()));
  }, []);

  // Reset show/year filters whenever the ITE event changes so stale filter
  // values from a previous event don't leave the charts empty.
  useEffect(() => {
    setGShow("All");
    setGYear(ALL_YEARS_DEFAULT);
  }, [selectedEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch yearly growth data per show.
  useEffect(() => {
    const params = selectedEvent ? { ite_event_id: selectedEvent } : {};
    setLoading(true);
    getGrowthData(params)
      .then(data => {
        if (!Array.isArray(data)) { setApiShows([]); setPendingCount(0); return; }

        // Each edition is its own show_id (…_2025, …_2026); strip the trailing
        // year so the editions of one competitor show form a single series. The
        // slug — not show_name — is the key, so two different shows that happen
        // to share a display name stay separate instead of silently merging.
        const seriesKey = (r) => r.show_id.replace(/_\d{4}$/, "") || r.show_name;

        // A row whose stats are all NULL is a placeholder written when the show
        // was scheduled (see shows.py) — the edition exists but hasn't been
        // enriched yet. Treating it as 0 companies invents a collapse to zero,
        // so it is excluded from the series and reported separately instead.
        const reported = data.filter(r => r.total !== null && r.total !== undefined);

        const byShow = {};
        reported.forEach(r => {
          const k = seriesKey(r);
          if (!byShow[k]) byShow[k] = { key: k, name: r.show_name, yearData: [] };
          if (byShow[k].yearData.some(d => d.y === r.year)) return;   // never double-count a year
          byShow[k].yearData.push({
            y:          r.year,
            total:      r.total          || 0,
            newCo:      r.new_companies  || 0,
            returning:  r.returning      || 0,
            dropped:    r.dropped        || 0,
            countries:  r.country_count  || 0,
            categories: r.category_count || 0,
          });
        });
        Object.values(byShow).forEach(s => s.yearData.sort((a, b) => a.y - b.y));
        setApiShows(Object.values(byShow));
        setPendingCount(data.length - reported.length);
      })
      .catch(() => { setApiShows([]); setPendingCount(0); })
      .finally(() => setLoading(false));
  }, [selectedEvent]);

  // ── Derived data ───────────────────────────────────────────────────────────

  const allShows = apiShows || [];
  // Options for the show filter — keyed by slug, labelled by display name.
  const SHOW_OPTIONS = allShows.map(s => ({ key: s.key, name: s.name }));

  // Derive available years from the selected show's data, or all shows when "All".
  // This ensures the year dropdown only offers years that have real data for
  // whatever show the user has picked.
  const yearSourceShows = gShow === "All" ? allShows : allShows.filter(s => s.key === gShow);
  const allDataYears    = [...new Set(yearSourceShows.flatMap(s => s.yearData.map(d => d.y)))].sort((a, b) => a - b);
  const ALL_YEARS       = allDataYears.length > 0 ? allDataYears : [CY - 3, CY - 2, CY - 1, CY];

  // Exclude the last year as a "from" start when multiple years exist — choosing
  // the last year as start produces a confusing "Year Range: 2024–2024" single-year label.
  const YEAR_OPTIONS   = ALL_YEARS.length > 1 ? ALL_YEARS.slice(0, -1) : ALL_YEARS;
  // Clamp stored gYear to a valid option so the dropdown never shows blank.
  const effectiveGYear = YEAR_OPTIONS.includes(gYear) ? gYear : (YEAR_OPTIONS[0] ?? gYear);
  const YEARS          = ALL_YEARS.filter(y => y >= effectiveGYear);
  const selShows       = gShow === "All" ? allShows : allShows.filter(s => s.key === gShow);

  // Aggregate a set of shows for one year. `shows` records how many contributed,
  // which is what makes an unequal year-on-year comparison detectable.
  // countries/categories are distinct counts, so they can be neither summed nor
  // averaged across shows — max is the honest floor for an event-level figure.
  const aggregate = (shows, y) => {
    const rows = shows.map(s => s.yearData.find(d => d.y === y)).filter(Boolean);
    return {
      y,
      shows:      rows.length,
      total:      rows.reduce((a, r) => a + r.total,     0),
      newCo:      rows.reduce((a, r) => a + r.newCo,     0),
      returning:  rows.reduce((a, r) => a + r.returning, 0),
      dropped:    rows.reduce((a, r) => a + r.dropped,   0),
      countries:  Math.max(0, ...rows.map(r => r.countries  || 0)),
      categories: Math.max(0, ...rows.map(r => r.categories || 0)),
    };
  };

  const byYear = YEARS.map(y => aggregate(selShows, y));

  // currYear/prevYear are derived from ALL shows (not filtered by gShow) so
  // the Country/Category tabs always reflect event-level years, not a single show.
  const eventDataYears = [...new Set(allShows.flatMap(s => s.yearData.map(d => d.y)))]
    .filter(y => YEARS.includes(y)).sort((a, b) => a - b);
  const currYear = eventDataYears[eventDataYears.length - 1];
  const prevYear = eventDataYears[eventDataYears.length - 2];

  // Fetch category YoY whenever the visible year range changes.
  useEffect(() => {
    if (!currYear) { setCatRows([]); return; }
    const base = { limit: 50 };
    if (selectedEvent) base.ite_event_id = selectedEvent;
    let alive = true;
    Promise.all([
      getTopCategories({ ...base, year: currYear }).catch(() => []),
      prevYear ? getTopCategories({ ...base, year: prevYear }).catch(() => []) : Promise.resolve([]),
    ]).then(([gCurr, gPrev]) => {
      if (!alive) return;
      setCatRows(mergeYoY(gPrev, gCurr, "category_name", "total_exhibitors").sort((a, b) => b.curr - a.curr).slice(0, 10));
    });
    return () => { alive = false; };
  }, [selectedEvent, currYear, prevYear]);

  // Country YoY with geopolitical alignment — fetched lazily when the Country tab
  // is active. Reuses the same /insights/geopolitical source as Market Insights
  // (Region + Friendly/Unfriendly), one call per year, merged into one row/country.
  useEffect(() => {
    if (growthTab !== "Country Growth" || !currYear) { setCountryRows([]); return; }
    const base = selectedEvent ? { ite_event_id: selectedEvent } : {};
    let alive = true;
    setCountryLoading(true);
    Promise.all([
      getGeopolitical({ ...base, year: currYear }).catch(() => []),
      prevYear ? getGeopolitical({ ...base, year: prevYear }).catch(() => []) : Promise.resolve([]),
    ]).then(([curr, prev]) => {
      if (!alive) return;
      setCountryRows(mergeGeoYoY(prev, curr));
      setCountryLoading(false);
    });
    return () => { alive = false; };
  }, [growthTab, selectedEvent, currYear, prevYear]);

  // ── Chart / table data ─────────────────────────────────────────────────────

  const EMPTY_AGG = { y: YEARS[YEARS.length - 1], shows:0, total:0, newCo:0, returning:0, dropped:0, countries:0, categories:0 };
  const latest    = byYear[byYear.length - 1] || EMPTY_AGG;
  const latestY   = latest.y;
  const prevY     = byYear.length > 1 ? byYear[byYear.length - 2].y : null;

  // Year-on-year is only meaningful across shows that reported BOTH years. When
  // coverage differs — an edition first tracked this year, or last year's not yet
  // enriched — totals move because the sample changed, not because the market
  // did. Comparisons run on that shared subset; the headline values stay the
  // true totals for the year, and the footer states the basis.
  const showsReportingBoth = (yA, yB) => (yA == null || yB == null) ? [] : selShows.filter(s =>
    s.yearData.some(d => d.y === yA) && s.yearData.some(d => d.y === yB)
  );
  const comparableShows = showsReportingBoth(latestY, prevY);
  const hasComparison = comparableShows.length > 0;
  const latestCmp = hasComparison ? aggregate(comparableShows, latestY) : null;
  const prevCmp   = hasComparison ? aggregate(comparableShows, prevY)   : null;
  // True when the comparison rests on fewer shows than the year actually has.
  const partialBasis = hasComparison && comparableShows.length < latest.shows;

  const deltaPct = (key) => {
    if (!hasComparison) return "No prior year";
    const p = pct(latestCmp[key], prevCmp[key]);
    return p == null ? "— vs prev" : `${p >= 0 ? "+" : ""}${p}% vs prev`;
  };
  const deltaAbs = (key) => {
    if (!hasComparison) return "No prior year";
    const d = latestCmp[key] - prevCmp[key];
    return `${d >= 0 ? "+" : ""}${d} vs prev`;
  };

  const KPIS = [
    { Icon:Building2,    v:latest.total.toLocaleString(),     d:deltaPct("total"),     sub:`Total Companies (${latestY})`, c:"#E8431A" },
    { Icon:Plus,         v:latest.newCo.toLocaleString(),     d:deltaPct("newCo"),     sub:`New Companies (${latestY})`,   c:"#22c55e" },
    { Icon:Repeat,       v:latest.returning.toLocaleString(), d:deltaPct("returning"), sub:`Returning (${latestY})`,       c:"#960659" },
    { Icon:TrendingDown, v:latest.dropped.toLocaleString(),   d:deltaPct("dropped"),   sub:`Dropped (${latestY})`,         c:"#C1121F" },
    { Icon:Globe,        v:latest.countries,                  d:deltaAbs("countries"),  sub:`Countries (${latestY})`,      c:"#960659" },
    { Icon:Package,      v:latest.categories,                 d:deltaAbs("categories"), sub:`Categories (${latestY})`,     c:"#F18F18" },
  ];

  // Keyed by slug, not display name, so same-named shows stay distinct series.
  const showSeries = YEARS.map(y => {
    const o = { y };
    selShows.forEach(s => { const d = s.yearData.find(x => x.y === y); o[s.key] = d ? d.total : null; });
    return o;
  });

  // Only shows with both years get a bar. A show with no prior year has no growth
  // rate — drawing it as 0% would read as "flat" rather than "not comparable".
  const growthByShow = selShows
    .map(s => {
      const a = s.yearData.find(x => x.y === latestY);
      const b = prevY == null ? null : s.yearData.find(x => x.y === prevY);
      return { name: s.name, g: a && b ? pct(a.total, b.total) : null };
    })
    .filter(s => s.g != null);

  // Each row's YoY rate uses the shows reporting both that year and the one
  // before it — same rule as the KPI strip, applied per consecutive pair. The
  // value column stays the true total for the year.
  const lifeTbl = (key) => byYear.map((d, i) => {
    const prevD = byYear[i - 1];
    const both  = prevD ? showsReportingBoth(d.y, prevD.y) : [];
    return {
      y:    d.y,
      v:    d[key],
      pctt: d.total ? +(d[key] / d.total * 100).toFixed(1) : 0,
      g:    both.length ? pct(aggregate(both, d.y)[key], aggregate(both, prevD.y)[key]) : null,
    };
  });

  const yoy = [
    { m:"Total Companies",       key:"total"      },
    { m:"New Companies",         key:"newCo"      },
    { m:"Returning Companies",   key:"returning"  },
    { m:"Dropped Companies",     key:"dropped"    },
    { m:"Countries Represented", key:"countries"  },
    { m:"Product Categories",    key:"categories" },
  ].map(r => {
    // Change columns come from the comparable subset, so they agree with the KPI
    // strip. Reading the previous year positionally and falling back on a falsy
    // check would silently swallow a legitimate prior value of 0.
    const vals = byYear.map(d => d[r.key]);
    return {
      m:    r.m,
      vals,
      ch:   hasComparison ? latestCmp[r.key] - prevCmp[r.key] : null,
      chp:  hasComparison ? pct(latestCmp[r.key], prevCmp[r.key]) : null,
    };
  });

  const isFiltered = !!(selectedEvent || gShow !== "All" || effectiveGYear !== ALL_YEARS[0]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageShell>
      <style>{`
        *::-webkit-scrollbar { width:5px }
        *::-webkit-scrollbar-thumb { background:#D8DADC; border-radius:3px }
        table.ct { width:100%; border-collapse:collapse; font-size:11px }
        table.ct th { text-align:left; padding:8px 10px; font-size:9px; font-weight:700; color:#818084;
          text-transform:uppercase; letter-spacing:0.4px; border-bottom:2px solid #D8DADC; background:#FAFAFA }
        table.ct td { padding:8px 10px; border-bottom:1px solid #F4F4F5 }
      `}</style>

      <Sidebar
        active="growth" onNav={nav}
        selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}
      />

      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

        <div style={{ padding:"16px 28px", background:"#fff", borderBottom:"1px solid #D8DADC" }}>
          <div style={{ fontSize:20, fontWeight:800 }}>Growth Analysis</div>
          <div style={{ fontSize:11, color:"#818084", marginTop:2 }}>
            Analyze competitor growth trends across shows, countries, categories and companies year over year.
          </div>
        </div>

        <FilterBar>
          <EventSelect
            value={selectedEvent || ""}
            onChange={e => setSelectedEvent(e.target.value || null)}
            events={events}
            eventsWithData={eventsWithData}
            allowAll
            style={filterSelectStyle(!!selectedEvent)}
          />

          <FilterSelect value={gShow} onChange={e => setGShow(e.target.value)} active={gShow !== "All"}>
            <option value="All">Competitor Show: All</option>
            {SHOW_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </FilterSelect>

          <FilterSelect value={effectiveGYear} onChange={e => setGYear(Number(e.target.value))} active={effectiveGYear !== ALL_YEARS[0]}>
            {YEAR_OPTIONS.map(y => {
              const maxYear = ALL_YEARS[ALL_YEARS.length - 1];
              return (
                <option key={y} value={y}>
                  {y < maxYear ? `Year Range: ${y}–${maxYear}` : `Year: ${y}`}
                </option>
              );
            })}
          </FilterSelect>

          {isFiltered && (
            <FilterClear onClick={() => { setSelectedEvent(null); setGShow("All"); setGYear(ALL_YEARS_DEFAULT); }} />
          )}

          <FilterSpacer />

          <button
            onClick={() => setExportCfg({
              title: "Export Growth Analysis",
              columns: GROWTH_COLUMNS,
              defaultKeys: DEFAULT_KEYS.growth,
              rows: byYear,
              filenameBase: `growth_${String(eventName).replace(/\s+/g, "_")}`,
              exportType: "growth_analysis",
              scope: { ite_event_id: selectedEvent || null, filters: { show: selShows[0]?.name || gShow, from_year: YEARS[0] } },
            })}
            disabled={byYear.length === 0}
            style={{ padding:"7px 12px", borderRadius:7, border:"1px solid #D8DADC", background:"#fff",
              fontSize:10, fontWeight:600, fontFamily:F, color:"#3F3F46",
              display:"flex", alignItems:"center", gap:4,
              cursor: byYear.length ? "pointer" : "default",
              opacity: byYear.length ? 1 : 0.5 }}>
            <Download size={12} /> Export
          </button>
        </FilterBar>

        {/* Tabs */}
        <div style={{ display:"flex", gap:0, borderBottom:"2px solid #D8DADC", padding:"0 28px", background:"#fff" }}>
          {GTABS.map(t => (
            <div key={t} onClick={() => setGrowthTab(t)} style={{
              padding:"8px 16px", fontSize:11.5, cursor:"pointer", marginBottom:-2,
              fontWeight: growthTab === t ? 700 : 400,
              color: growthTab === t ? "#E8431A" : "#818084",
              borderBottom: growthTab === t ? "2px solid #E8431A" : "2px solid transparent",
            }}>
              {t}
            </div>
          ))}
        </div>

        <div style={{ flex:1, overflow:"auto", padding:"16px 28px 32px" }}>

          {loading ? (
            <div style={{ padding:"60px", textAlign:"center", color:"#818084", fontSize:13 }}>
              Loading growth data…
            </div>
          ) : allShows.length === 0 ? (
            <EmptyTab label="No growth data for this event yet." />
          ) : (
            <>
              {byYear.length <= 1 ? (
                <NoticeBanner>
                  Only one year of data — growth comparisons will appear once a second year is recorded.
                </NoticeBanner>
              ) : !hasComparison ? (
                <NoticeBanner>
                  No competitor show reports both {prevY} and {latestY}, so year-on-year
                  comparisons aren't available for this selection.
                </NoticeBanner>
              ) : null}

              {pendingCount > 0 && (
                <NoticeBanner>
                  {pendingCount} show edition{pendingCount === 1 ? " is" : "s are"} awaiting
                  enrichment and {pendingCount === 1 ? "is" : "are"} excluded — totals cover
                  enriched editions only.
                </NoticeBanner>
              )}

              {/* KPI strip */}
              <div style={{ display:"flex", gap:10, marginBottom:18 }}>
                {KPIS.map((k, i) => (
                  <div key={i} style={{ flex:1, background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"14px" }}>
                    <div style={{ width:30, height:30, borderRadius:7, background:k.c + "18",
                      display:"flex", alignItems:"center", justifyContent:"center", marginBottom:8 }}>
                      {k.Icon && <k.Icon size={16} color={k.c} />}
                    </div>
                    <div style={{ fontSize:20, fontWeight:800, fontFamily:M, letterSpacing:-1 }}>{k.v}</div>
                    <div style={{ fontSize:9.5, fontWeight:700, marginTop:2,
                      color: String(k.d).startsWith("+") ? "#16a34a" : String(k.d).startsWith("-") ? "#C1121F" : "#818084" }}>
                      {k.d}
                    </div>
                    <div style={{ fontSize:9, color:"#818084", marginTop:3 }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {growthTab === "Competitor Show Growth" && (
                <CompetitorShowTab
                  byYear={byYear} selShows={selShows} showSeries={showSeries}
                  growthByShow={growthByShow} yoy={yoy} YEARS={YEARS}
                  hasComparison={hasComparison} partialBasis={partialBasis}
                />
              )}
              {growthTab === "Country Growth" && (
                <CountryGrowthTab rows={countryRows} loading={countryLoading} currYear={currYear} prevYear={prevYear} />
              )}
              {growthTab === "Category Growth" && (
                <CategoryGrowthTab catRows={catRows} currYear={currYear} />
              )}
              {growthTab === "New / Returning / Dropped" && (
                <NRDTab
                  byYear={byYear}
                  newTbl={lifeTbl("newCo")}
                  retTbl={lifeTbl("returning")}
                  dropTbl={lifeTbl("dropped")}
                />
              )}

              <div style={{ textAlign:"right", fontSize:8.5, color:"#AFABAB", marginTop:14 }}>
                {gShow === "All" ? `All ${SHOW_OPTIONS.length} tracked shows` : (selShows[0]?.name || gShow)}
                {" · "}{YEARS[0]}–{YEARS[YEARS.length - 1]}
                {partialBasis && ` · YoY based on ${comparableShows.length} of ${latest.shows} shows reporting both years`}
                {pendingCount > 0 && ` · ${pendingCount} edition${pendingCount === 1 ? "" : "s"} pending enrichment`}
                {" · "}Source: Exhibitor Lists &amp; Official Directories
              </div>
            </>
          )}

        </div>
      </div>

      <ExportModal
        config={exportCfg}
        createdBy={currentUser || currentRole}
        onClose={() => setExportCfg(null)}
        onExported={() => showToast("Growth analysis exported")}
      />
    </PageShell>
  );
}
